import { match } from 'ts-pattern'

import { quickbooksSyncErrorContent } from './quickbooksSyncErrorContent'

import {
  customerFieldLabel,
  failingFieldGroups,
  parseFailingFields
} from 'lib/integrations/syncErrorFields'
import {
  QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED,
  FailingField,
  FailingFieldGroup,
  FailingFieldProblemMix,
  SyncErrorTaskInput
} from './types'
import { quoteFieldLabel } from './records/quote/quoteFieldLabels'
import { syncErrorOperationLabel } from './syncErrorOperation'

export { parseFailingFields }

type FailingFieldScope = 'ADDRESS' | 'GENERAL'

type FailingFieldSummary = {
  scope: FailingFieldScope
  problem: FailingFieldProblemMix
}

export const fieldLabel = (
  inputOrFieldName: SyncErrorTaskInput | string,
  fieldName?: string
): string => {
  if (typeof inputOrFieldName === 'string') {
    return customerFieldLabel(inputOrFieldName)
  }

  return inputOrFieldName.entityType === 'QUOTE'
    ? quoteFieldLabel(fieldName ?? '')
    : customerFieldLabel(fieldName ?? '')
}

export const entityLabel = (entityType: string): string =>
  sentenceCase(entityType)

export const serviceLabel = (externalService: string): string =>
  externalService.replaceAll('_', ' ')

export const recordName = (input: SyncErrorTaskInput): string | undefined =>
  input.entitySnapshot?.name?.trim() || undefined

export const syncErrorTitle = (input: SyncErrorTaskInput): string =>
  `${recordName(input) ?? serviceLabel(input.externalService)}: ${syncErrorOperationLabel(input) ?? `${entityLabel(input.entityType).toLowerCase()} ${syncAction(input)}`} failed`

export const syncErrorEventLabel = (input: SyncErrorTaskInput): string =>
  syncErrorOperationLabel(input)
    ? `${sentenceCase(syncErrorOperationLabel(input) ?? '')} failed`
    : `${entityLabel(input.entityType)} failed to ${syncAction(input)}`

export const syncErrorDescription = (input: SyncErrorTaskInput): string => {
  const quickbooksContent = quickbooksSyncErrorContent(input)
  if (quickbooksContent) return quickbooksContent.summary
  const fields = parseFailingFields(input.errorMessage)

  if (!fields) {
    return input.errorMessage
  }

  return match(failingFieldSummary(fields))
    .with(
      { scope: 'ADDRESS', problem: 'MISSING' },
      () => 'Missing required address info'
    )
    .with(
      { scope: 'ADDRESS', problem: 'INVALID' },
      () => 'Invalid address info'
    )
    .with(
      { scope: 'ADDRESS', problem: 'MIXED' },
      () => 'Invalid or missing address info'
    )
    .with({ problem: 'MISSING' }, () => 'Missing required fields')
    .with({ problem: 'INVALID' }, () => 'Invalid fields')
    .with({ problem: 'MIXED' }, () => 'Invalid or missing fields')
    .exhaustive()
}

export const syncErrorExplanation = (input: SyncErrorTaskInput): string =>
  quickbooksSyncErrorContent(input)?.explanation ??
  `${recordName(input) ?? entityLabel(input.entityType)} couldn't be ${completedSyncAction(input)} automatically because ${syncErrorReason(input)} in ${serviceLabel(input.externalService)}.`

export const syncErrorDetail = (
  input: SyncErrorTaskInput
): string | undefined =>
  quickbooksSyncErrorContent(input)?.instruction ??
  (parseFailingFields(input.errorMessage) ? undefined : input.errorMessage)

export const syncErrorFieldGroups = (
  input: SyncErrorTaskInput
): FailingFieldGroup[] =>
  failingFieldGroups(parseFailingFields(input.errorMessage) ?? [], fieldName =>
    fieldLabel(input, fieldName)
  )

const syncAction = (input: SyncErrorTaskInput): string =>
  match(input.direction)
    .with('PULL', () => 'import')
    .otherwise(() => 'sync')

const completedSyncAction = (input: SyncErrorTaskInput): string =>
  match(input.direction)
    .with('PULL', () => 'imported')
    .otherwise(() => 'synced')

const syncErrorReason = (input: SyncErrorTaskInput): string => {
  const fields = parseFailingFields(input.errorMessage)

  if (!fields) {
    return errorCodeReason(input.errorCode)
  }

  return match(failingFieldSummary(fields))
    .with(
      { scope: 'ADDRESS', problem: 'MISSING' },
      () => 'required address details are missing'
    )
    .with(
      { scope: 'ADDRESS', problem: 'INVALID' },
      () => 'some address details are invalid'
    )
    .with(
      { scope: 'ADDRESS', problem: 'MIXED' },
      () => 'some address details are missing or invalid'
    )
    .with({ problem: 'MISSING' }, () => 'required details are missing')
    .with({ problem: 'INVALID' }, () => 'some details are invalid')
    .with({ problem: 'MIXED' }, () => 'some details are missing or invalid')
    .exhaustive()
}

const failingFieldSummary = (fields: FailingField[]): FailingFieldSummary => ({
  scope: fields.every(isAddressField) ? 'ADDRESS' : 'GENERAL',
  problem: failingFieldProblems(fields)
})

const isAddressField = (field: FailingField): boolean =>
  field.name === 'address' || field.name.startsWith('address.')

const failingFieldProblems = (
  fields: FailingField[]
): FailingFieldProblemMix => {
  const hasMissing = fields.some(field => field.problem === 'MISSING')
  const hasInvalid = fields.some(field => field.problem === 'INVALID')

  return match({ hasMissing, hasInvalid })
    .with({ hasMissing: true, hasInvalid: true }, () => 'MIXED' as const)
    .with({ hasInvalid: true }, () => 'INVALID' as const)
    .otherwise(() => 'MISSING' as const)
}

const errorCodeReason = (errorCode: string): string =>
  match(errorCode)
    .with(
      'PULL_CREATE_FAILED',
      'PULL_UPDATE_FAILED',
      'PULL_PROJECTION_FAILED',
      'PROJECTION_FAILED',
      () => 'the record could not be mapped'
    )
    .with('INVALID_FIELD', () => 'one or more fields are invalid')
    .with('DUPLICATE_VALUE', () => 'a field duplicates an existing record')
    .with('ENTITY_IS_DELETED', () => 'the record has been deleted')
    .with(
      'INSUFFICIENT_ACCESS_OR_READONLY',
      () => 'Sequence does not have access to the record'
    )
    .with(
      'INVALID_OR_NULL_FOR_RESTRICTED_PICKLIST',
      () => 'a field holds a value outside its allowed options'
    )
    .with('PROCESSING_HALTED', () => 'processing was halted')
    .with('INTERNAL_SERVER_ERROR', () => 'of an unexpected error')
    .with(
      'MISSING_CONDITIONAL_REQUIRED_PROPERTY',
      () => 'a field HubSpot requires for this deal stage is missing'
    )
    .with('HUBSPOT_RECORD_NOT_FOUND', () => 'the deal no longer exists')
    .with(
      'HUBSPOT_PAYLOAD_TOO_LARGE',
      () => "the file is too large for this portal's plan"
    )
    .with('HUBSPOT_BAD_REQUEST', () => 'HubSpot refused the update')
    .with(
      QUICKBOOKS_AUTO_APPLY_CREDIT_ENABLED,
      () => 'automatically applying credits is turned on'
    )
    .otherwise(() => 'of an error')

const sentenceCase = (upperSnakeCase: string): string => {
  const words = upperSnakeCase.toLowerCase().replaceAll('_', ' ')

  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`
}
